/*──────────────────────────────────────────────────────────────
 * src/pages/TicketGrid.jsx – fully regenerated 2025-12-22
 *
 * • NEW: Bulk mark Billed/Unbilled from grid selection (Admin & Owner).
 * • ExplorerModal added (same UX as HomePage).
 * • Ticket column numeric sort; advanced export keeps Lease/Well/Type.
 * • Hours table clears immediately on range change.
 * • External access route + token-gated pattern maintained.
 * • Admin Part/Item filter (by Part # or Item Description).
 * • “Last Change Date” & “Last Change By” in grid + exports.
 *   - Last Change Date = lastUpdatedTimestamp || createdTimestamp (mm/dd/yy HH:MM)
 *   - Last Change By   = lastUser || UserID
 * • FIX: routeRoot aware navigation prevents subdomain duplication/append.
 * • UI: Compact, no-wrap bulk actions (Bill / Unbill / Clear) with inline count.
 * • UI UPDATE 2025-11-26:
 *   - When viewing Unbilled list, only show the Bill button.
 *   - When viewing Billed list, only show the Unbill button.
 *   - High-contrast button text in light mode (Bill label always readable).
 * • EXPORT UPDATE 2025-12-22:
 *   - Export numeric values as numbers (not text) for quick summation:
 *     Basic Export: Total
 *     Advanced Export: Cost, LineTotal, TicketTotal (Qty already numeric)
 *   - Add time log to exports (Basic: aggregated per ticket; Advanced: per row)
 *──────────────────────────────────────────────────────────────*/

import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTheme, useUser } from "ogcommon";
import { AgGridReact } from "ag-grid-react";
import debounce from "lodash.debounce";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faClock,
  faSearch,
  faPlus,
  faFileExport,
  faTimes,
  faFolderOpen,
  faFilter,
  faXmark,
  faCheck,
  faBan,
  faBroom,
} from "@fortawesome/free-solid-svg-icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import ExplorerModal from "../components/ExplorerModal";
import { baseUrl } from "./config";
import { fetchTicketList } from "../utils/tickets";

/* ---------- local storage keys ---------- */
const STORAGE_KEYS = {
  SHOW_UNBILLED: "ticketGridShowUnbilled",
  FILTER_MODEL: "ticketGridFilterModel",
  QUICK_FILTER: "ticketGridQuickFilter",
  PART_FILTER: "ticketGridPartFilter",
};

/* ---------- date helpers ---------- */
const mmddyy = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

const mmddyyTime = (ts) => {
  if (!ts) return "";
  const s = ts.includes(" ") ? ts.replace(" ", "T") : ts;
  const d = new Date(s);
  if (isNaN(+d)) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yy} ${hh}:${mi}`;
};

/* ---------- last-change derivations ---------- */
const deriveLastChangeDate = (t) =>
  t?.lastUpdatedTimestamp || t?.createdTimestamp || "";
const deriveLastChangeBy = (t) => t?.lastUser || t?.UserID || "";

/* ---------- match helper for admin part filter ---------- */
const ticketMatchesPartQuery = (ticket, queryRaw) => {
  if (!queryRaw) return true;
  const q = String(queryRaw).trim().toLowerCase();
  if (!q) return true;
  const items = ticket?.Items || [];
  for (const it of items) {
    const id = (it.ItemID ?? "").toString().toLowerCase();
    const desc = (it.ItemDescription ?? "").toLowerCase();
    if (id.includes(q) || desc.includes(q)) return true;
  }
  return false;
};

/* ---------- export helpers ---------- */
const toNumOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const buildTicketTimeLog = (t) => {
  const items = t?.Items || [];
  const parts = [];
  for (const it of items) {
    if (it?.UseStartStop !== "Y") continue;
    const start = mmddyyTime(it.Start ?? "");
    const stop = mmddyyTime(it.Stop ?? "");
    const qty = toNumOrNull(it.Quantity);
    const id = it.ItemID ?? "";
    const desc = it.ItemDescription ?? "";
    const line = [
      id ? `${id}` : "",
      desc ? `- ${desc}` : "",
      start ? `| ${start}` : "",
      stop ? `→ ${stop}` : "",
      qty !== null ? `(${qty} hrs)` : "",
    ]
      .filter(Boolean)
      .join(" ");
    if (line.trim()) parts.push(line.trim());
  }
  return parts.join("; ");
};

const TicketGrid = () => {
  /* ---------- routing / context ---------- */
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { userRole, userID, companyName } = useUser();

  // External access routing + token
  const { subdomain } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const tokenAccess = searchParams.has("token"); // reserved for future

  // IMPORTANT: all navigations use this to avoid “subdomain append/dup”
  const routeRoot = subdomain ? `/${subdomain}` : "";

  /* ---------- state ---------- */
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highestTicketNumber, setHighestTicketNumber] = useState(null);
  const [searchQuery, setSearchQuery] = useState(
    localStorage.getItem(STORAGE_KEYS.QUICK_FILTER) || "",
  );
  const [showUnbilled, setShowUnbilled] = useState(
    JSON.parse(localStorage.getItem(STORAGE_KEYS.SHOW_UNBILLED) || "true"),
  );

  const [exportModal, setExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("basic");

  // Hours modal
  const [hoursModal, setHoursModal] = useState(false);
  const [hoursData, setHoursData] = useState([]);
  const [hoursLoading, setHoursLoading] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: todayStr, end: todayStr });

  // Explorer modal
  const [explorerOpen, setExplorerOpen] = useState(false);

  // Admin Part/Item filter
  const [partFilter, setPartFilter] = useState(
    localStorage.getItem(STORAGE_KEYS.PART_FILTER) || "",
  );
  const debouncedPersistPartFilter = useMemo(
    () =>
      debounce((val) => {
        localStorage.setItem(STORAGE_KEYS.PART_FILTER, val);
      }, 250),
    [],
  );

  // company metadata (header + perms merge)
  const [companyInfo, setCompanyInfo] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState(null);

  const storedCredentials = JSON.parse(
    localStorage.getItem("credentials") || "{}",
  );
  const { perms: storedPerms = {} } = storedCredentials;

  const [perms, setPerms] = useState(() => ({
    hasOgFiles: false,
    UseFieldTicket: false,
    ...storedPerms,
  }));

  const [writeAccess, setWriteAccess] = useState(
    () => !!storedCredentials.perms?.ogfieldticket?.write,
  );

  const gridRef = useRef();
  const [gridApi, setGridApi] = useState(null);

  // Selection & bulk actions
  const [selectedCount, setSelectedCount] = useState(0);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'bill' | 'unbill'
  const [toast, setToast] = useState(null);

  /* ---------- clear Hours table if date range changes ---------- */
  useEffect(() => {
    setHoursData([]);
    setHoursLoading(false);
  }, [range]);

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  /* ---------- fetch company metadata (external route support) ---------- */
  const fetchCompanyInfo = useCallback(async () => {
    if (!subdomain) return;
    setMetaLoading(true);
    setMetaError(null);
    try {
      const r = await fetch(
        `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          subdomain,
        )}`,
      );
      const js = await r.json();
      if (!Array.isArray(js) || !js.length) throw new Error("Client not found");
      const single = js[0];

      let serverPerms = {};
      if (typeof single.perms === "string") {
        try {
          serverPerms = JSON.parse(single.perms);
        } catch (e) {
          console.warn("Invalid perms JSON", e);
        }
      } else if (single.perms && typeof single.perms === "object") {
        serverPerms = single.perms;
      }

      setCompanyInfo(single);
      setPerms((prev) => ({
        ...prev,
        ...serverPerms,
      }));

      const w =
        typeof serverPerms?.ogfieldticket?.write !== "undefined"
          ? !!serverPerms.ogfieldticket.write
          : null;
      if (w !== null) setWriteAccess(w);
    } catch (err) {
      setMetaError(err.message || "Unknown error");
    } finally {
      setMetaLoading(false);
    }
  }, [subdomain]);

  useEffect(() => {
    fetchCompanyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompanyInfo]);

  const resolvedCompanyName =
    companyInfo?.company || companyName || subdomain || "";

  /* ---------- navigation helpers (routeRoot-safe) ---------- */
  const openDetails = useCallback(
    (ticket) => {
      if (gridApi) {
        localStorage.setItem(
          STORAGE_KEYS.FILTER_MODEL,
          JSON.stringify(gridApi.getFilterModel()),
        );
      }
      navigate(`${routeRoot}/view-field-ticket`, {
        state: { ticket, highestTicketNumber },
      });
    },
    [gridApi, navigate, highestTicketNumber, routeRoot],
  );

  /* ---------- tooltip renderer ---------- */
  const tooltipRenderer = (p) => (
    <span title={p.value} className="block truncate">
      {p.value}
    </span>
  );

  /* ---------- column definitions ---------- */
  const columnDefs = useMemo(() => {
    const ticketComparator = (a, b) => Number(a) - Number(b);
    const detailsRenderer = (p) => (
      <button
        onClick={() => openDetails(p.data)}
        title="Details"
        className={`cursor-pointer ${
          theme === "dark" ? "text-blue-400" : "text-blue-600"
        }`}
      >
        <FontAwesomeIcon icon={faEye} />
      </button>
    );

    return [
      // Selection checkbox column
      {
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 44,
        pinned: "left",
        suppressMenu: true,
        sortable: false,
        filter: false,
        resizable: false,
        hide: !["A", "O"].includes(userRole), // show for Admin/Owner only
      },
      {
        headerName: "Ticket↓",
        field: "Ticket",
        width: 80,
        sortable: true,
        sort: "desc",
        filter: "agNumberColumnFilter",
        comparator: ticketComparator,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Date",
        field: "TicketDate",
        width: 110,
        sortable: true,
        filter: "agDateColumnFilter",
        valueFormatter: (p) => mmddyy(p.value),
      },
      {
        headerName: "Lease/Well",
        field: "LeaseWell",
        flex: 1.3,
        minWidth: 160,
        sortable: true,
        filter: true,
        cellRenderer: tooltipRenderer,
      },
      {
        headerName: "Type",
        field: "JobDescription",
        width: 125,
        sortable: true,
        filter: true,
        cellRenderer: tooltipRenderer,
      },
      {
        headerName: "User",
        field: "UserID",
        width: 70,
        sortable: true,
        filter: true,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Total $",
        field: "TotalAmount",
        width: 90,
        sortable: true,
        filter: true,
        valueFormatter: (p) =>
          p.value ? `$${parseFloat(p.value).toFixed(2)}` : "$0.00",
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Billed",
        field: "Billed",
        width: 80,
        sortable: true,
        filter: true,
        cellRenderer: (p) => (p.value === "Y" ? "Yes" : "No"),
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Images",
        field: "HasImage",
        width: 75,
        sortable: true,
        filter: true,
        cellRenderer: (p) => (p.value ? "Yes" : "No"),
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Notes",
        field: "Note",
        flex: 2,
        minWidth: 200,
        sortable: true,
        filter: true,
        wrapText: true,
        cellRenderer: tooltipRenderer,
        cellStyle: {
          whiteSpace: "normal",
          lineHeight: "1.25",
          padding: "4px",
        },
      },
      /* ── Last-change metadata ─────────────────────────── */
      {
        headerName: "Last Change Date",
        field: "LastChangeDate",
        width: 160,
        sortable: true,
        filter: true,
        valueFormatter: (p) => mmddyyTime(p.value),
        tooltipValueGetter: (p) => mmddyyTime(p.value),
      },
      {
        headerName: "Last Change By",
        field: "LastChangeBy",
        width: 130,
        sortable: true,
        filter: true,
        cellRenderer: tooltipRenderer,
      },
      /* ─────────────────────────────────────────────────── */
      {
        headerName: "Details",
        width: 70,
        cellRenderer: detailsRenderer,
        cellStyle: { textAlign: "center" },
      },
    ];
  }, [theme, openDetails, userRole]);

  /* ---------- fetch tickets ---------- */
  useEffect(() => {
    (async () => {
      try {
        let nextID = null;

        const ticketsEndpoint =
          subdomain && subdomain.length
            ? `${baseUrl}/api/tickets.php?company=${encodeURIComponent(
                subdomain,
              )}`
            : `${baseUrl}/api/tickets.php`;

        // Shared defensive loader: `list` is null on any failure (never a
        // misparsed array), so .filter() below can't throw on an HTML/error body.
        const { list } = await fetchTicketList(ticketsEndpoint);
        const data = list || [];

        const processed = data
          .filter((t) => {
            if (t.isNextTicketID) {
              nextID = t.Ticket;
              return false;
            }
            return true;
          })
          .map((t) => {
            const total = t.Items?.reduce(
              (s, i) =>
                s + (parseFloat(i.Quantity) || 0) * (parseFloat(i.Cost) || 0),
              0,
            );
            const leaseWell = t.WellID
              ? `${t.LeaseName} / ${t.WellID}`
              : t.LeaseName;

            const LastChangeDate = deriveLastChangeDate(t);
            const LastChangeBy = deriveLastChangeBy(t);

            return {
              ...t,
              TotalAmount: total,
              LeaseWell: leaseWell,
              HasImage: t.Images?.length > 0 || t.hasImages === "Y",
              LastChangeDate,
              LastChangeBy,
            };
          });

        setTickets(processed);
        setHighestTicketNumber(
          nextID ||
            (processed.length
              ? Math.max(...processed.map((t) => +t.Ticket)) + 1 + ""
              : null),
        );
        setLoading(false);
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    })();
  }, [subdomain]);

  /* ---------- search & toggle ---------- */
  const debouncedSearch = useMemo(
    () =>
      debounce((q) => {
        setSearchQuery(q);
        localStorage.setItem(STORAGE_KEYS.QUICK_FILTER, q);
        gridApi?.setQuickFilter(q);
      }, 200),
    [gridApi],
  );
  const handleSearch = (e) => debouncedSearch(e.target.value);

  const toggleBilled = () =>
    setShowUnbilled((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEYS.SHOW_UNBILLED, JSON.stringify(next));
      return next;
    });

  /* ---------- visible tickets ---------- */
  const visibleTickets = useMemo(() => {
    const filtered = tickets.filter((t) => {
      const unbilled = t.Billed !== "Y";
      const own = t.UserID === userID;
      const roleOK = userRole !== "P" || own;
      const billOK =
        userRole === "P" ? unbilled : showUnbilled ? unbilled : !unbilled;

      const partOK =
        userRole === "A" ? ticketMatchesPartQuery(t, partFilter) : true;

      return roleOK && billOK && partOK;
    });
    return filtered;
  }, [tickets, showUnbilled, userRole, userID, partFilter]);

  /* ---------- export ---------- */
  const runExport = useCallback(
    (mode) => {
      if (!gridApi) return;

      const rows = [];
      const totalRows = gridApi.getDisplayedRowCount();
      for (let i = 0; i < totalRows; i++) {
        rows.push(gridApi.getDisplayedRowAtIndex(i).data);
      }

      const data =
        mode === "basic"
          ? rows.map((t) => ({
              Ticket: t.Ticket,
              Date: mmddyy(t.TicketDate),
              Lease: t.LeaseName,
              Well: t.WellID ?? "",
              Type: t.JobDescription,
              UserID: t.UserID,
              Billed: t.Billed === "Y" ? "Yes" : "No",
              // NUMERIC TOTAL (NOT TEXT)
              Total: toNumOrNull(t.TotalAmount) ?? 0,
              Note: t.Note ?? "",
              TimeLog: buildTicketTimeLog(t),
              LastChangeDate: mmddyyTime(deriveLastChangeDate(t)),
              LastChangeBy: deriveLastChangeBy(t),
            }))
          : rows.flatMap((t) =>
              (t.Items?.length ? t.Items : [{}]).map((it) => {
                const qty = toNumOrNull(it.Quantity);
                const cost = toNumOrNull(it.Cost);
                const lineTotal =
                  qty !== null && cost !== null ? qty * cost : null;

                const startStr =
                  it.UseStartStop === "Y" ? mmddyyTime(it.Start ?? "") : "";
                const stopStr =
                  it.UseStartStop === "Y" ? mmddyyTime(it.Stop ?? "") : "";

                const obj = {
                  Ticket: t.Ticket,
                  Date: mmddyy(t.TicketDate),
                  Lease: t.LeaseName,
                  Well: t.WellID ?? "",
                  Type: t.JobDescription,
                  LeasePropertyNum: t.LeasePropertyNum ?? "",
                  WellPropertyNum: t.WellPropertyNum ?? "",
                  ItemID: it.ItemID ?? "",
                  Desc: it.ItemDescription ?? "",
                  UOM: it.UOM ?? "",
                  // HOURS / QTY AS NUMBER FOR EXCEL-SAFE SUMMATION
                  Qty: qty,
                  // NUMERIC COST / TOTALS (NOT TEXT)
                  Cost: cost,
                  LineTotal: lineTotal,
                  TicketTotal: toNumOrNull(t.TotalAmount) ?? 0,
                  UserID: t.UserID,
                  Note: t.Note ?? "",
                  // TIME LOG (PER ROW)
                  Start: startStr || null,
                  Stop: stopStr || null,
                  TimeLog:
                    it.UseStartStop === "Y" && (startStr || stopStr)
                      ? `${startStr}${
                          startStr && stopStr ? " → " : ""
                        }${stopStr}`
                      : null,
                  LastChangeDate: mmddyyTime(deriveLastChangeDate(t)),
                  LastChangeBy: deriveLastChangeBy(t),
                };

                return obj;
              }),
            );

      const ws = XLSX.utils.json_to_sheet(data);

      // Set reasonable column widths
      ws["!cols"] = Object.keys(data[0] || {}).map((key) => ({
        wch: Math.min(
          40,
          Math.max(
            key.length + 2,
            ...data.map((r) => String(r?.[key] ?? "").length + 2),
          ),
        ),
      }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tickets");
      XLSX.writeFile(
        wb,
        `tickets_${mode}_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    },
    [gridApi],
  );

  /* ---------- selection helpers ---------- */
  const getSelectedTicketIDs = useCallback(() => {
    if (!gridApi) return [];
    const nodes = gridApi.getSelectedNodes() || [];
    return nodes
      .map((n) => n?.data?.Ticket)
      .filter((id) => typeof id !== "undefined" && id !== null)
      .map((id) => String(id));
  }, [gridApi]);

  const onSelectionChanged = useCallback(() => {
    setSelectedCount(getSelectedTicketIDs().length);
  }, [getSelectedTicketIDs]);

  const clearSelection = useCallback(() => {
    gridApi?.deselectAll();
    setSelectedCount(0);
  }, [gridApi]);

  const performBulkUpdate = useCallback(
    async (toBilled) => {
      const ids = getSelectedTicketIDs();
      if (!ids.length) return;

      setBulkLoading(true);
      try {
        const endpoint =
          subdomain && subdomain.length
            ? `${baseUrl}/api/tickets.php?bulk=1&company=${encodeURIComponent(
                subdomain,
              )}`
            : `${baseUrl}/api/tickets.php?bulk=1`;

        const payload = {
          tickets: ids.map((s) => parseInt(s, 10)),
          Billed: toBilled ? "Y" : "N",
          lastUser: userID,
        };

        const res = await fetch(endpoint, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const js = await res.json().catch(() => ({}));
        if (!res.ok || js?.error) {
          throw new Error(js?.error || js?.message || "Bulk update failed");
        }

        const idSet = new Set(ids.map(String));
        const nowISO = new Date().toISOString();

        // Optimistic local update
        setTickets((prev) =>
          prev.map((t) =>
            idSet.has(String(t.Ticket))
              ? {
                  ...t,
                  Billed: toBilled ? "Y" : "N",
                  LastChangeBy: userID,
                  LastChangeDate: nowISO,
                  lastUser: userID,
                  lastUpdatedTimestamp: nowISO,
                }
              : t,
          ),
        );

        clearSelection();
        setToast({
          type: "success",
          message: `${ids.length} ticket${ids.length > 1 ? "s" : ""} ${
            toBilled ? "billed" : "unbilled"
          }.`,
        });
      } catch (err) {
        console.error(err);
        setToast({
          type: "error",
          message: err.message || "Bulk update failed",
        });
      } finally {
        setBulkLoading(false);
        setConfirmModal(false);
        setConfirmAction(null);
      }
    },
    [getSelectedTicketIDs, subdomain, userID, clearSelection],
  );

  /* ---------- grid ready ---------- */
  const onGridReady = ({ api }) => {
    setGridApi(api);
    if (searchQuery) api.setQuickFilter(searchQuery);
    const saved = localStorage.getItem(STORAGE_KEYS.FILTER_MODEL);
    if (saved) api.setFilterModel(JSON.parse(saved));
  };

  const saveFilters = () =>
    gridApi &&
    localStorage.setItem(
      STORAGE_KEYS.FILTER_MODEL,
      JSON.stringify(gridApi.getFilterModel()),
    );

  /* ---------- hours calculation ---------- */
  const calcHours = useCallback(() => {
    setHoursLoading(true);
    const startD = new Date(`${range.start}T00:00:00`);
    const endD = new Date(`${range.end}T23:59:59`);
    const map = new Map();

    tickets.forEach((t) => {
      const td = new Date(`${t.TicketDate}T00:00:00`);
      if (td < startD || td > endD) return;

      t.Items?.forEach((it) => {
        if (it.UseStartStop !== "Y") return;
        const qty = parseFloat(it.Quantity) || 0;
        if (!map.has(it.ItemID)) {
          map.set(it.ItemID, {
            ItemID: it.ItemID,
            ItemDescription: it.ItemDescription,
            HoursForDateRange: qty,
          });
        } else {
          map.get(it.ItemID).HoursForDateRange += qty;
        }
      });
    });

    setHoursData(
      Array.from(map.values()).sort(
        (a, b) => b.HoursForDateRange - a.HoursForDateRange,
      ),
    );
    setHoursLoading(false);
  }, [tickets, range]);

  /* ---------- render ---------- */
  return (
    <div
      className={`min-h-screen ${
        theme === "dark"
          ? "bg-gray-900 text-white"
          : "bg-gray-100 text-gray-900"
      }`}
    >
      {/* ExplorerModal */}
      <ExplorerModal
        open={explorerOpen}
        onClose={() => setExplorerOpen(false)}
        theme={theme}
        companyName={resolvedCompanyName}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded shadow ${
            toast.type === "error"
              ? "bg-red-600 text-white"
              : "bg-green-600 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="container mx-auto px-2 pb-4 mt-6">
        {loading ? (
          <div className="flex justify-center items-center h-[60vh]">
            <div className="animate-spin h-32 w-32 border-t-2 border-b-2 rounded-full border-blue-500" />
          </div>
        ) : (
          <>
            {/* toolbar (space-conservative, single-line, scrollable if needed) */}
            <div
              className="flex items-center gap-2 md:gap-2 mb-2 whitespace-nowrap overflow-x-auto flex-nowrap"
              /* Avoid wrapping; allow gentle horizontal scroll on small screens */
            >
              {(writeAccess === true || writeAccess === "true") && (
                <button
                  onClick={() =>
                    navigate(
                      `${routeRoot}/create-field-ticket/${highestTicketNumber}`,
                    )
                  }
                  className={`inline-flex items-center font-bold py-2 px-3 rounded-full shadow ${
                    theme === "dark"
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-white hover:bg-gray-100 text-blue-600"
                  }`}
                  title="Create new ticket"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  <span className="hidden sm:inline">New</span>
                </button>
              )}

              {/* Explorer */}
              {perms.hasOgFiles && (
                <button
                  onClick={() => setExplorerOpen(true)}
                  title="Lease File Explorer"
                  className={`inline-flex items-center font-bold py-2 px-3 rounded-full shadow ${
                    theme === "dark"
                      ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                      : "bg-white hover:bg-gray-100 text-indigo-700"
                  }`}
                >
                  <FontAwesomeIcon icon={faFolderOpen} className="mr-2" />
                  <span className="hidden sm:inline">Explorer</span>
                </button>
              )}

              {/* Export */}
              <button
                onClick={() => setExportModal(true)}
                className={`inline-flex items-center font-bold py-2 px-3 rounded-full shadow ${
                  theme === "dark"
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-white hover:bg-gray-100 text-green-700"
                }`}
                title="Export"
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                <span className="hidden sm:inline">Export</span>
              </button>

              {["A", "O"].includes(userRole) && (
                <button
                  onClick={() => setHoursModal(true)}
                  className={`inline-flex items-center font-bold py-2 px-3 rounded-full shadow ${
                    theme === "dark"
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "bg-white hover:bg-gray-100 text-purple-700"
                  }`}
                  title="Hours by item"
                >
                  <FontAwesomeIcon icon={faClock} className="mr-2" />
                  <span className="hidden sm:inline">Hours</span>
                </button>
              )}

              {/* Quick search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  defaultValue={searchQuery}
                  onChange={handleSearch}
                  className={`pl-8 pr-2 py-2 border rounded h-[38px] ${
                    theme === "dark"
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-800"
                  }`}
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
                />
              </div>

              {/* Admin-only Part/Item filter */}
              {userRole === "A" && (
                <div className="relative">
                  <input
                    type="text"
                    placeholder='Part # or Item (e.g., "Labor - Joe Baeza")'
                    value={partFilter}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPartFilter(val);
                      debouncedPersistPartFilter(val);
                    }}
                    className={`pl-14 pr-8 py-2 border rounded h-[38px] w-[20rem] md:w-[24rem] ${
                      theme === "dark"
                        ? "bg-gray-800 border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-800"
                    }`}
                    title="Admin-only: filter by Part # or Item Description"
                  />
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
                  />
                  <button
                    onClick={() => {
                      debouncedPersistPartFilter.cancel?.();
                      setPartFilter("");
                      localStorage.setItem(STORAGE_KEYS.PART_FILTER, "");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    title="Clear part filter"
                    aria-label="Clear part filter"
                  >
                    <FontAwesomeIcon icon={faXmark} className="text-gray-500" />
                  </button>
                </div>
              )}

              {/* END CAP: Compact bulk controls + visibility toggle (no wrap) */}
              <div className="ml-auto flex items-center gap-1">
                {/* Admin/Owner bulk cluster (tight, state-aware) */}
                {["A", "O"].includes(userRole) && (
                  <div
                    className={`flex items-center gap-1 px-1.5 py-1 rounded-full ${
                      theme === "dark" ? "bg-gray-800" : "bg-white"
                    } shadow`}
                  >
                    {/* When viewing UNBILLED list → show Bill only */}
                    {showUnbilled && (
                      <button
                        disabled={!selectedCount || bulkLoading}
                        onClick={() => {
                          setConfirmAction("bill");
                          setConfirmModal(true);
                        }}
                        className={`inline-flex items-center h-8 text-sm px-2.5 rounded font-semibold
      ${
        !selectedCount || bulkLoading
          ? "opacity-50 cursor-not-allowed"
          : theme === "dark"
            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
            : "border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
      }
    `}
                        title={
                          selectedCount
                            ? `Bill ${selectedCount} selected`
                            : "Select tickets to bill"
                        }
                      >
                        <FontAwesomeIcon icon={faCheck} className="mr-1" />
                        <span className="hidden sm:inline">Bill</span>
                        <span className="sm:ml-1">({selectedCount})</span>
                      </button>
                    )}

                    {/* When viewing BILLED list → show Unbill only */}
                    {!showUnbilled && (
                      <button
                        disabled={!selectedCount || bulkLoading}
                        onClick={() => {
                          setConfirmAction("unbill");
                          setConfirmModal(true);
                        }}
                        className={`inline-flex items-center h-8 text-sm px-2.5 rounded font-semibold
      border
      ${
        !selectedCount || bulkLoading
          ? "opacity-50 cursor-not-allowed"
          : theme === "dark"
            ? "border-amber-400 hover:bg-amber-700/20 text-amber-200"
            : "border-amber-600 hover:bg-amber-50 text-amber-700"
      }
    `}
                        title={
                          selectedCount
                            ? `Unbill ${selectedCount} selected`
                            : "Select tickets to unbill"
                        }
                      >
                        <FontAwesomeIcon icon={faBan} className="mr-1" />
                        <span className="hidden sm:inline">Unbill</span>
                        <span className="sm:ml-1">({selectedCount})</span>
                      </button>
                    )}

                    {/* Clear (kept minimal) */}
                    <button
                      disabled={!selectedCount || bulkLoading}
                      onClick={clearSelection}
                      className={`inline-flex items-center h-8 text-sm px-2.5 rounded ${
                        !selectedCount || bulkLoading
                          ? "opacity-50 cursor-not-allowed"
                          : theme === "dark"
                            ? "bg-gray-700 hover:bg-gray-600 text-white"
                            : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                      }`}
                      title="Clear selection"
                    >
                      <FontAwesomeIcon icon={faBroom} className="mr-1" />
                      <span className="hidden sm:inline">Clear</span>
                    </button>
                  </div>
                )}

                {/* Show Billed / Unbilled toggle (kept concise) */}
                {userRole !== "P" && (
                  <button
                    onClick={toggleBilled}
                    className={`h-8 text-sm px-3 rounded font-semibold flex items-center ${
                      theme === "dark"
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                    title={showUnbilled ? "Show billed" : "Show unbilled"}
                  >
                    <FontAwesomeIcon
                      icon={showUnbilled ? faEye : faEyeSlash}
                      className="mr-1"
                    />
                    <span className="hidden sm:inline">
                      {showUnbilled ? "Show Billed" : "Show Unbilled"}
                    </span>
                    <span className="sm:hidden">
                      {showUnbilled ? "Billed" : "Unbilled"}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* grid */}
            <div
              className={`ag-theme-alpine ${
                theme === "dark" ? "ag-theme-alpine-dark" : ""
              }`}
              style={{ height: "calc(100vh - 320px)", width: "100%" }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={visibleTickets}
                columnDefs={columnDefs}
                defaultColDef={{
                  flex: 1,
                  minWidth: 70,
                  sortable: true,
                  filter: true,
                  resizable: true,
                  wrapText: true,
                }}
                getRowId={(p) => String(p.data?.Ticket)}
                enableBrowserTooltips
                onGridReady={onGridReady}
                onFilterChanged={saveFilters}
                onSelectionChanged={onSelectionChanged}
                pagination
                paginationPageSize={25}
                rowSelection={
                  ["A", "O"].includes(userRole) ? "multiple" : "single"
                }
                suppressRowClickSelection
                rowHeight={40}
              />
            </div>
          </>
        )}
      </div>

      {/* export modal */}
      {exportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            className={`w-80 rounded-lg shadow-xl p-6 ${
              theme === "dark" ? "bg-gray-800 text-white" : "bg-white"
            }`}
          >
            <h2 className="text-lg font-semibold mb-4">Export format</h2>

            <label className="flex items-center gap-2 mb-2 cursor-pointer">
              <input
                type="radio"
                value="basic"
                checked={exportMode === "basic"}
                onChange={(e) => setExportMode(e.target.value)}
              />
              Basic – one row / ticket
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="advanced"
                checked={exportMode === "advanced"}
                onChange={(e) => setExportMode(e.target.value)}
              />
              Advanced – include items
            </label>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setExportModal(false)}
                className={`px-4 py-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-1" />
                Cancel
              </button>
              <button
                onClick={() => {
                  runExport(exportMode);
                  setExportModal(false);
                }}
                className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-1" />
                Export
              </button>
            </div>
          </div>
        </div>
      )}

      {/* hours modal */}
      {hoursModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            className={`w-[34rem] max-h-[90vh] overflow-auto rounded-lg shadow-xl p-6 ${
              theme === "dark" ? "bg-gray-800 text-white" : "bg-white"
            }`}
          >
            <h2 className="text-lg font-semibold mb-4">Hours by Item</h2>

            {/* quick links */}
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                ["Today", 0],
                ["Last 7 d", 6],
                ["MTD", new Date().getDate() - 1],
                ["Prev Month", "prev"],
                [
                  "YTD",
                  (() => {
                    const d = new Date();
                    return Math.floor(
                      (d - new Date(d.getFullYear(), 0, 1)) / 8.64e7,
                    );
                  })(),
                ],
              ].map(([label, offset]) => (
                <button
                  key={label}
                  onClick={() => {
                    const now = new Date();
                    let s,
                      e = now;
                    if (offset === "prev") {
                      const firstPrev = new Date(
                        now.getFullYear(),
                        now.getMonth() - 1,
                        1,
                      );
                      s = firstPrev;
                      e = new Date(now.getFullYear(), now.getMonth(), 0);
                    } else {
                      s = new Date(now);
                      s.setDate(now.getDate() - offset);
                    }
                    setRange({
                      start: s.toISOString().slice(0, 10),
                      end: e.toISOString().slice(0, 10),
                    });
                  }}
                  className={`px-3 py-1 rounded ${
                    theme === "dark"
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* date inputs */}
            <div className="flex gap-2 mb-4">
              <label className="flex flex-col text-sm">
                Start
                <input
                  type="date"
                  value={range.start}
                  onChange={(e) =>
                    setRange((r) => ({ ...r, start: e.target.value }))
                  }
                  className={`rounded p-1 ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-100"
                  }`}
                />
              </label>
              <label className="flex flex-col text-sm">
                End
                <input
                  type="date"
                  value={range.end}
                  onChange={(e) =>
                    setRange((r) => ({ ...r, end: e.target.value }))
                  }
                  className={`rounded p-1 ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-100"
                  }`}
                />
              </label>
              <button
                onClick={calcHours}
                className="ml-auto px-4 py-2 rounded bg-purple-600 hover:bg-purple-700 text-white h-10 self-end"
              >
                Run
              </button>
            </div>

            {/* results */}
            {hoursLoading ? (
              <p>Loading…</p>
            ) : hoursData.length ? (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="font-semibold">
                    <th className="border-b py-1 text-left">Item ID</th>
                    <th className="border-b py-1 text-left">Description</th>
                    <th className="border-b py-1 text-right">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {hoursData.map((r) => (
                    <tr key={r.ItemID}>
                      <td className="border-b py-1 pr-2">{r.ItemID}</td>
                      <td className="border-b py-1 pr-2">
                        {r.ItemDescription}
                      </td>
                      <td className="border-b py-1 text-right">
                        {(+r.HoursForDateRange).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="italic">No data for selected range.</p>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setHoursModal(false)}
                className={`px-4 py-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* confirm bulk billed/unbilled modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            className={`w-[34rem] rounded-lg shadow-xl p-6 ${
              theme === "dark" ? "bg-gray-800 text-white" : "bg-white"
            }`}
          >
            <h2 className="text-lg font-semibold mb-2">
              {confirmAction === "bill" ? "Mark as Billed" : "Mark as Unbilled"}
            </h2>
            <p className="text-sm mb-3">
              You are about to update <strong>{selectedCount}</strong> ticket
              {selectedCount > 1 ? "s" : ""}. Continue?
            </p>
            {/* brief preview of selected ticket numbers */}
            <div
              className={`text-xs rounded p-2 mb-4 border ${
                theme === "dark" ? "border-gray-700" : "border-gray-200"
              } max-h-32 overflow-auto`}
            >
              {(() => {
                if (!gridApi) return null;
                const nodes = gridApi.getSelectedNodes() || [];
                const ids = nodes.map((n) => n?.data?.Ticket).filter(Boolean);
                const preview = ids.slice(0, 20).join(", ");
                return (
                  <span title={ids.join(", ")}>
                    {preview}
                    {ids.length > 20 ? " …" : ""}
                  </span>
                );
              })()}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setConfirmModal(false);
                  setConfirmAction(null);
                }}
                className={`px-4 py-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-1" />
                Cancel
              </button>
              <button
                disabled={bulkLoading}
                onClick={() => performBulkUpdate(confirmAction === "bill")}
                className={`px-4 py-2 rounded text-white ${
                  confirmAction === "bill"
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-amber-600 hover:bg-amber-700"
                }`}
              >
                {bulkLoading
                  ? "Working…"
                  : confirmAction === "bill"
                    ? "Mark Billed"
                    : "Mark Unbilled"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(TicketGrid);
